import React from "react";
import PageHero, { FeatureList } from "@/components/PageHero";
import { CTAFooter } from "@/pages/Prestations";
import { CatalogPackages } from "@/components/CatalogPackages";
import { useSeo, JsonLd } from "@/lib/seo";
import { HeartHandshake, Smile, Volume2, Sparkles, Check, Headphones, Award, Crown } from "lucide-react";

export default function Seniors() {
  useSeo({
    title: "Animation musicale EHPAD & résidence senior Nord | My Animation",
    description:
      "Animations musicales adaptées pour EHPAD, résidences seniors et événements conviviaux dans le Nord, le Pas-de-Calais et les Hauts-de-France. Forfaits dès 350 €.",
    path: "/seniors",
    keywords: "animation musicale EHPAD Nord, animation résidence senior Lille, animation EHPAD Hauts-de-France, animation intergénérationnelle",
  });

  const serviceLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Animation musicale EHPAD & résidence senior",
    provider: { "@type": "LocalBusiness", name: "My Animation" },
    areaServed: "Hauts-de-France, Nord, Pas-de-Calais, Belgique",
    description: "Animations musicales adaptées, conviviales et chaleureuses pour EHPAD, résidences seniors et événements intergénérationnels.",
  };

  return (
    <>
      <JsonLd data={serviceLd} />
      <PageHero
        eyebrow="Résidences Seniors"
        title={<>Des animations <span className="italic gold-text">chaleureuses.</span></>}
        subtitle="Animations musicales adaptées, conviviales et chaleureuses pour EHPAD, résidences seniors et événements intergénérationnels dans le Nord, le Pas-de-Calais et les Hauts-de-France."
        img="/assets/seniors/seniors-01.jpg"
      />

      <section className="py-24 md:py-32 px-6 md:px-10 lg:px-14 max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5 reveal">
            <span className="eyebrow">Notre engagement</span>
            <h2 className="font-serif-luxe text-4xl md:text-5xl lg:text-6xl mt-4 leading-[1.02]">
              Un moment <br/><span className="italic gold-text">partagé.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 reveal">
            <p className="text-white/70 text-base md:text-lg leading-relaxed">
              My Animation propose des animations musicales adaptées aux résidences
              seniors et EHPAD : répertoire intergénérationnel, volume sonore maîtrisé,
              ambiance conviviale et participative. L'objectif : créer un moment
              chaleureux qui rassemble résidents, familles et personnels.
            </p>
            <div className="mt-10">
              <FeatureList items={[
                "Répertoire adapté (chansons françaises, variétés)",
                "Volume sonore maîtrisé et respectueux",
                "Animation participative (jeux musicaux)",
                "Karaoké & blind test adaptés",
                "Matériel professionnel discret",
                "Intervention dans tout le Nord & Pas-de-Calais",
                "Devis administratif sur demande",
                "Coordination avec l'équipe d'animation",
              ]} />
            </div>
          </div>
        </div>
      </section>

      <CatalogPackages
        category="senior"
        title="Nos forfaits résidences seniors / EHPAD"
        subtitle={(n) => (<>{n || "Nos"} format{n > 1 ? "s" : ""}, <span className="italic gold-text">une même exigence.</span></>)}
        icons={[Headphones, Award, Crown]}
        testidPrefix="seniors-pack-"
        featuredBadgeLabel="Le plus demandé"
        ctaLabel="Demander ce forfait"
        footerNote="Tarifs TTC · Devis administratif sur demande"
        comparisonExtraCols={[
          { header: "Micro sans fil", cell: () => <Check size={18} strokeWidth={1.5} className="text-[#DCB457]" /> },
        ]}
      />

      <section className="py-24 md:py-32 px-6 md:px-10 lg:px-14 bg-[#080808] border-y border-[#DCB457]/15">
        <div className="max-w-[1400px] mx-auto">
          <div className="mb-14 reveal">
            <span className="eyebrow">Format des animations</span>
            <h2 className="font-serif-luxe text-4xl md:text-5xl mt-3">Conviviales, <span className="italic gold-text">participatives.</span></h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { i: Smile, t: "Après-midi musical", d: "Variétés françaises, chansons des années 50-80, ambiance détente." },
              { i: HeartHandshake, t: "Soirée intergénérationnelle", d: "Familles & résidents réunis autour de la musique." },
              { i: Volume2, t: "Volume maîtrisé", d: "Sonorisation adaptée, respect du confort auditif." },
              { i: Sparkles, t: "Événements spéciaux", d: "Fêtes de fin d'année, anniversaires d'établissement, kermesses." },
            ].map(({ i: Icon, t, d }) => (
              <div key={t} className="card-luxe p-7 reveal">
                <Icon size={28} strokeWidth={1} className="text-[#DCB457] mb-5" />
                <h3 className="font-serif-luxe text-2xl">{t}</h3>
                <p className="text-white/55 mt-2 text-sm leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTAFooter />
    </>
  );
}

